import { z } from 'zod'
import type { ReviewMode } from './review-mode'
import type { TelemetryTask } from './telemetry'

export type MessageRole = 'user' | 'assistant'

/**
 * Role used in API requests to the chat completion endpoint.
 * Includes 'system' for system prompts in addition to user/assistant.
 */
export type ApiMessageRole = 'user' | 'assistant' | 'system'

/**
 * LLM model identifier sent to the chat completion API.
 *
 * Kept as `string` so model names can come dynamically from backend config
 * (e.g. mygenassist) without requiring frontend deploys for new model IDs.
 */
export type ModelKey = string

export const DEFAULT_MODEL: ModelKey = 'claude-4.7-opus'

/**
 * Send-time context captured when the user submitted the message.
 * Stored on user messages to enable full context restoration on edit/reuse.
 */
export interface SendContext {
  systemPrompt?: string
  reviewMode?: ReviewMode | null
  model?: ModelKey
  requiresFullDocument?: boolean
}

// Zod lives here (unlike other types) because the chat history read back from localStorage must be validated at runtime.
export const MessageSourceSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
})

/** A file-pool file the LLM used to generate a response. Empty `sections` means the whole file. */
export const MessageSourceSchema = z.object({
  fileId: z.string(),
  fileName: z.string(),
  sections: z.array(MessageSourceSectionSchema),
})

export const MessageSourcesSchema = z.array(MessageSourceSchema)

export type MessageSourceSection = z.infer<typeof MessageSourceSectionSchema>
export type MessageSource = z.infer<typeof MessageSourceSchema>

export interface ChatMessage {
  id: string
  role: MessageRole
  content: string
  timestamp: number
  streamState: 'streaming' | 'done' | 'error'
  streamPreviewContent?: string
  infoNote?: string
  /** Word comment/thread ids for View links, index-aligned with summary segments (inserts, replies, or null). */
  wordCommentIds?: (string | null)[]
  sendContext?: SendContext
  /** Set on assistant messages so an Insert click can be reported against the request that produced it. */
  telemetryTask?: TelemetryTask
  /** Empty array: no sources. Undefined on a finished assistant message: sources couldn't be loaded, so the menu shows an error. */
  sources?: MessageSource[]
}

/**
 * Message format expected by the /chat/chat_completion endpoint.
 */
export interface ApiChatMessage {
  role: ApiMessageRole
  content: string
}

/**
 * A single entry in the filepool context — maps a file to its selected sections.
 * - section_ids: [] → entire file is selected
 * - section_ids: [...] → only the listed sections are selected
 */
export interface FilepoolContextEntry {
  file_id: string
  section_ids: string[]
}

/**
 * A single entry in the current document context. Discriminated by `type` so each
 * variant only carries the fields it actually needs — narrow with `entry.type === '...'`
 * before reading `content`/`ids`.
 * - mouse_selection: HTML selection content or QC paragraph content (`id` + `content`)
 * - full_document: plain text of the full working document
 * - full_document_paragraph: plain text of a paragraph within the full document
 * - section: plain text (headings) or HTML (tables) of a specific selected section
 * - table_html: whole-table HTML haystack, added in comment mode whenever a selection
 *   touches table paragraphs (see buildTableReferences). Has no `id` — it exists purely
 *   to give the LLM row/column context, not to anchor a comment. Per-cell target entries
 *   for paragraphs already covered by a table_html reference are omitted from context
 *   (see omitTableCellTargets).
 * - section_ids: agent mode only - ids of selected working-doc sections, no inline content;
 *   the agent resolves them on demand via the `get_section`/`get_document_outline` skills
 * - full_document_included: agent mode only - marks the entire working document as selected,
 *   with no inline content; the agent calls corresponding skills if it needs the text
 *
 * `table_context` is a legacy field, kept optional for backward compatibility with older
 * senders; the frontend no longer produces it (superseded by `table_html`).
 * `col_heading`: absent for caption/header rows; `null` for footer rows; string for data rows (omitted if empty).
 * `row_heading`: absent for caption rows; `null` for header/footer rows; string for data rows (omitted if col-0 or empty).
 */
export type CurrentDocumentContextEntry =
  | { type: 'mouse_selection'; content: string; id?: string | null }
  | { type: 'full_document'; content: string }
  | { type: 'full_document_paragraph'; content: string; id: string }
  | { type: 'section'; content: string; id: string }
  | {
      type: 'table_html'
      content: string
      table_context?: {
        table_caption: string
        col_heading?: string | null
        row_heading?: string | null
      }
    }
  | { type: 'section_ids'; ids: string[] }
  | { type: 'full_document_included' }

/**
 * A single entry in the current document comments.
 */
export interface CurrentDocumentCommentEntry {
  comment_id: string
  paragraph_id?: string
  search_text?: string | null
  comment_text: string
  author_email?: string | null
  author_name?: string | null
  /** ISO 8601 timestamp from Word.Comment.creationDate */
  creation_date?: string | null
  resolved?: boolean | null
}

/**
 * Context passed alongside messages to the chat completion endpoint.
 */
export interface ChatCompletionContext {
  filepool_context?: FilepoolContextEntry[]
  current_document_context?: CurrentDocumentContextEntry[]
  current_document_comments?: CurrentDocumentCommentEntry[]
}

/**
 * Request body for POST /chat/chat_completion
 */
export interface ChatCompletionRequest {
  messages: ApiChatMessage[]
  model: ModelKey
  context: ChatCompletionContext
  stream: boolean
  review_mode?: ReviewMode
  /** @deprecated Legacy shim for old BE; removed once BE #142 is deployed everywhere. */
  response_format?: string
}

/**
 * New anchored comment from review mode — paragraph anchor plus optional search text.
 */
export interface ReviewComment {
  paragraphId: string
  searchText: string | null
  commentText: string
}

/**
 * Reply to an existing Word comment thread - maps to Comment.reply via addCommentReplies.
 * Detected when the API returns comment_id (BE strips paragraph_id / search_text on replies).
 */
export interface CommentReply {
  commentId: string
  commentText: string
}

/** Ordered review item from chat completion metadata — preserves API index for View links. */
export type ReviewCommentItem =
  | { kind: 'insert'; comment: ReviewComment }
  | { kind: 'reply'; reply: CommentReply }
  | { kind: 'skipped' }

export type { ChatCompletionResponse } from '../api/data-connectors/dtos/chat'
